import { Alert, Box, Button, Group, MultiSelect, Stack, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconCalendarPlus, IconEdit, IconTrash } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react';
import { Location, Practitioner, PractitionerRole } from '@medplum/fhirtypes';
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { alert } from '@/services/alert';
import { SchedulingPageProvider } from '@/pages/SchedulingPage/SchedulingPageContext';
import { useAppointmentTypeContext } from './useAppointmentTypeContext';
import { loadFilterResources } from '@/pages/SchedulingPage/utils/availability';
import { mapPractitionersToWithRole } from '@/pages/SchedulingPage/utils/schedulingShared';
import { PractitionerWithRole } from '@/pages/SchedulingPage/types';
import { DateRangeFilter } from '../../shared/DateRangeFilter';
import { PatientCentralPageHeader } from '../../shared/PatientCentralPageHeader';
import { TimeBlockDrawer } from './TimeBlockDrawer';
import { filterTimeBlockRows, TimeBlockRow } from './timeBlocks.utils';
import { useTimeBlockSlots } from './useTimeBlockSlots';

interface FilterResources {
  practitioners: Practitioner[];
  practitionerRoles: PractitionerRole[];
  locations: Location[];
}

/** Distinct {value,label} option lists derived from the loaded rows, so filters always match the data. */
function deriveFilterOptions(rows: TimeBlockRow[]): {
  locationOptions: { value: string; label: string }[];
  procedureTypeOptions: { value: string; label: string }[];
  providerOptions: { value: string; label: string }[];
} {
  const locations = new Map<string, string>();
  const procedureTypes = new Map<string, string>();
  const providers = new Map<string, string>();
  for (const row of rows) {
    if (row.locationRef) {
      locations.set(row.locationRef, row.locationName ?? row.locationRef);
    }
    for (const type of row.procedureTypes) {
      if (type.code) {
        procedureTypes.set(type.code, type.display ?? type.code);
      }
    }
    if (row.practitionerRef) {
      providers.set(row.practitionerRef, row.providerName);
    }
  }
  const toOptions = (m: Map<string, string>): { value: string; label: string }[] =>
    [...m.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  return {
    locationOptions: toOptions(locations),
    procedureTypeOptions: toOptions(procedureTypes),
    providerOptions: toOptions(providers),
  };
}

export function TimeBlocksTab(): ReactElement {
  const {
    currentUserPermissions: { canManageSchedules },
  } = useCurrentUser();

  // Gate before mounting the content so its data hooks (slots, appointment types, filter resources)
  // never fire for users without schedule-management permission — the guard isn't just a visual hide.
  if (!canManageSchedules) {
    return (
      <Alert color="yellow" title="Not authorized" m="md">
        You don't have permission to manage procedure time blocks.
      </Alert>
    );
  }

  return <TimeBlocksContent />;
}

function TimeBlocksContent(): ReactElement {
  const medplum = useMedplum();
  const schedulingContextValue = useAppointmentTypeContext();

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() => [
    dayjs().startOf('day').toDate(),
    dayjs().add(60, 'day').endOf('day').toDate(),
  ]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedProcedureTypes, setSelectedProcedureTypes] = useState<string[]>([]);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);

  const [drawerOpened, setDrawerOpened] = useState(false);
  const [editingSlotId, setEditingSlotId] = useState<string | null>(null);

  const [filterResources, setFilterResources] = useState<FilterResources | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadFilterResources(medplum)
      .then((res) => {
        if (!cancelled) {
          setFilterResources(res);
        }
      })
      .catch(() => {
        /* filter dropdowns degrade gracefully; rows still derive their own options */
      });
    return () => {
      cancelled = true;
    };
  }, [medplum]);

  const practitionersWithRole: PractitionerWithRole[] = useMemo(
    () =>
      filterResources
        ? mapPractitionersToWithRole(filterResources.practitioners, filterResources.practitionerRoles)
        : [],
    [filterResources]
  );

  const startDate = dateRange[0] ?? dayjs().startOf('day').toDate();
  const endDate = dateRange[1] ?? dayjs().add(60, 'day').endOf('day').toDate();
  const { rows, isLoading, refresh } = useTimeBlockSlots({ startDate, endDate });

  const { locationOptions, procedureTypeOptions, providerOptions } = useMemo(() => deriveFilterOptions(rows), [rows]);

  const filteredRows = useMemo(
    () =>
      filterTimeBlockRows(rows, {
        locationRefs: selectedLocations,
        procedureTypeCodes: selectedProcedureTypes,
        practitionerRefs: selectedProviders,
      }),
    [rows, selectedLocations, selectedProcedureTypes, selectedProviders]
  );

  const handleNew = useCallback(() => {
    setEditingSlotId(null);
    setDrawerOpened(true);
  }, []);

  const handleEdit = useCallback((slotId: string) => {
    setEditingSlotId(slotId);
    setDrawerOpened(true);
  }, []);

  const handleDelete = useCallback(
    (row: TimeBlockRow) => {
      modals.openConfirmModal({
        title: 'Delete time block',
        children: (
          <Text size="sm">
            Delete the {row.dateLabel} {row.startTimeLabel}–{row.endTimeLabel} block for {row.providerName}? This cannot
            be undone.
          </Text>
        ),
        labels: { confirm: 'Delete', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: async () => {
          try {
            await medplum.deleteResource('Slot', row.slotId);
            alert.success('Time block deleted');
            refresh();
          } catch (error) {
            alert.error(`Couldn't delete time block: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        },
      });
    },
    [medplum, refresh]
  );

  const handleSaveSuccess = useCallback(() => {
    setDrawerOpened(false);
    setEditingSlotId(null);
    refresh();
  }, [refresh]);

  const columns = useMemo<MRT_ColumnDef<TimeBlockRow>[]>(
    () => [
      { accessorKey: 'dateLabel', header: 'Date', size: 110 },
      { accessorKey: 'startTimeLabel', header: 'Start', size: 90 },
      { accessorKey: 'endTimeLabel', header: 'End', size: 90 },
      { accessorKey: 'providerName', header: 'Provider', size: 150 },
      { accessorFn: (row) => row.locationName ?? 'Virtual', header: 'Location', id: 'location', size: 120 },
      {
        accessorFn: (row) => (row.procedureTypes ?? []).map((t) => t.display ?? t.code).join(', '),
        header: 'Procedure types',
        id: 'procedureTypes',
        size: 150,
        Cell: ({ row }) => {
          const types = row.original.procedureTypes ?? [];
          if (types.length === 0) {
            return (
              <Text size="sm" c="dimmed">
                —
              </Text>
            );
          }
          const labels = types.map((t) => t.display ?? t.code).filter(Boolean) as string[];
          return (
            <Tooltip
              label={labels.join(', ')}
              multiline
              maw={320}
              withArrow
              events={{ hover: true, focus: true, touch: true }}
            >
              <Text size="sm" style={{ cursor: 'default', width: 'fit-content' }}>
                {labels.length} type{labels.length === 1 ? '' : 's'}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        accessorFn: (row) => row.guideline ?? '',
        header: 'Guideline',
        id: 'guideline',
        size: 130,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) {
            return (
              <Text size="sm" c="dimmed">
                —
              </Text>
            );
          }
          return (
            <Tooltip label={value} multiline maw={320} withArrow>
              <Text size="sm" truncate="end">
                {value}
              </Text>
            </Tooltip>
          );
        },
      },
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data: filteredRows,
    getRowId: (row) => row.slotId,
    state: { isLoading, showGlobalFilter: false },
    initialState: { density: 'xs' as const },
    enableRowSelection: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enablePagination: false,
    enableColumnActions: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableSorting: true,
    enableRowActions: true,
    positionActionsColumn: 'last',
    displayColumnDefOptions: { 'mrt-row-actions': { size: 110, header: 'Actions' } },
    // Keep the table inside its own box: bound to the parent width and scroll horizontally if needed,
    // rather than widening the page and clipping the Actions column off-screen.
    mantineTableContainerProps: { style: { maxWidth: '100%', overflowX: 'auto' } },
    renderRowActions: ({ row }) => (
      <Group gap="xs" wrap="nowrap">
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconEdit size={14} />}
          onClick={() => handleEdit(row.original.slotId)}
        >
          Edit
        </Button>
        <Button
          variant="subtle"
          color="red"
          size="compact-sm"
          leftSection={<IconTrash size={14} />}
          onClick={() => handleDelete(row.original)}
        >
          Delete
        </Button>
      </Group>
    ),
    mantineTableProps: { highlightOnHover: true },
  });

  return (
    <SchedulingPageProvider value={schedulingContextValue}>
      <Stack gap="xs" p="sm">
        <PatientCentralPageHeader
          title="Procedure Time Blocks"
          actions={
            <Button size="xs" leftSection={<IconCalendarPlus size={14} />} onClick={handleNew}>
              New Block
            </Button>
          }
        />

        <Group align="flex-end" gap="xs" wrap="wrap">
          <DateRangeFilter value={dateRange} onChange={setDateRange} size="xs" />
          <MultiSelect
            label="Locations"
            placeholder="All locations"
            data={locationOptions}
            value={selectedLocations}
            onChange={setSelectedLocations}
            clearable
            searchable
            size="xs"
            w={170}
          />
          <MultiSelect
            label="Procedure types"
            placeholder="All types"
            data={procedureTypeOptions}
            value={selectedProcedureTypes}
            onChange={setSelectedProcedureTypes}
            clearable
            searchable
            size="xs"
            w={170}
          />
          <MultiSelect
            label="Providers"
            placeholder="All providers"
            data={providerOptions}
            value={selectedProviders}
            onChange={setSelectedProviders}
            clearable
            searchable
            size="xs"
            w={170}
          />
        </Group>

        {/* miw=0 lets this flex child shrink so the table scrolls inside its box instead of widening the page */}
        <Box w="100%" miw={0} style={{ overflowX: 'auto' }}>
          <MantineReactTable table={table} />
        </Box>

        <TimeBlockDrawer
          opened={drawerOpened}
          onClose={() => {
            setDrawerOpened(false);
            setEditingSlotId(null);
          }}
          slotIdToEdit={editingSlotId}
          onSaveSuccess={handleSaveSuccess}
          practitioners={practitionersWithRole}
        />
      </Stack>
    </SchedulingPageProvider>
  );
}
